import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Mic, Star, ShoppingCart, Users, TrendingUp,
  Play, Loader2, Filter, Globe, Sparkles, Volume2, Plus,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 flex items-center gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/5`}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <div>
        <p className="text-xl font-bold text-white heading-font">{value}</p>
        <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      </div>
    </div>
  );
}

function VoiceCard({ voice, onPurchase, purchasing }) {
  const [expanded, setExpanded] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const genderIcon = voice.gender === 'female' ? 'text-pink-400' : voice.gender === 'male' ? 'text-blue-400' : 'text-gray-400';
  const styleColors = {
    'Luxury Concierge': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    'Warm Host': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    'Professional Manager': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    'Friendly Guide': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    'Calm Storyteller': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    'Energetic Presenter': 'bg-red-500/10 text-red-400 border-red-500/20',
  };

  return (
    <Card className="bg-white/[0.03] border-white/5 hover:border-white/10 transition-all group" data-testid={`voice-card-${voice.voice_id}`}>
      <CardContent className="p-0">
        {/* Header */}
        <div className="p-4 pb-3">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-[#F7931A]/20 border border-purple-500/15 flex items-center justify-center">
                <Mic className={`w-5 h-5 ${genderIcon}`} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-100 heading-font">{voice.name}</h3>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Globe className="w-2.5 h-2.5 text-gray-500" />
                  <span className="text-[10px] text-gray-500">{voice.accent}</span>
                  <span className="text-gray-700 text-[10px]">|</span>
                  <span className={`text-[10px] ${genderIcon}`}>{voice.gender}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-[#F7931A] heading-font">{voice.price_hermes}</p>
              <p className="text-[8px] text-gray-600 uppercase">$HERMES</p>
            </div>
          </div>

          <p className="text-[11px] text-gray-400 leading-relaxed mb-2.5">{voice.description}</p>

          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={`text-[9px] ${styleColors[voice.style] || 'border-white/10 text-gray-400'}`}>
              {voice.style}
            </Badge>
            {voice.rating > 0 && (
              <div className="flex items-center gap-0.5">
                <Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />
                <span className="text-[10px] text-amber-400">{voice.rating.toFixed(1)}</span>
                <span className="text-[9px] text-gray-600">({voice.ratings_count})</span>
              </div>
            )}
            <span className="text-[9px] text-gray-600">{voice.purchases} purchases</span>
            {voice.provider === 'mock' && (
              <Badge variant="outline" className="text-[8px] border-gray-700 text-gray-500">Preview</Badge>
            )}
          </div>
        </div>

        {/* Sample text (expandable) */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-4 py-2 flex items-center justify-between text-[10px] text-gray-500 hover:text-gray-300 hover:bg-white/[0.02] transition-colors border-t border-white/[0.03]"
        >
          <span className="flex items-center gap-1.5">
            <Volume2 className="w-3 h-3" /> Sample
          </span>
          <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>

        {expanded && (
          <div className="px-4 pb-3 space-y-2">
            <p className="text-[11px] text-gray-300 italic leading-relaxed bg-white/[0.02] rounded-lg p-2.5 border border-white/[0.03]">
              &ldquo;{voice.sample_text}&rdquo;
            </p>
            <Button
              size="sm"
              variant="outline"
              className="w-full text-xs h-7 gap-1.5 border-purple-500/20 text-purple-400 hover:bg-purple-500/10"
              disabled={previewing}
              data-testid={`preview-play-${voice.voice_id}`}
              onClick={async () => {
                setPreviewing(true);
                try {
                  const res = await fetch(`${API}/api/marketplace/voices/${voice.voice_id}/preview`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
                  });
                  const data = await res.json();
                  if (data.audio_base64) {
                    const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
                    audio.volume = 0.7;
                    audio.onended = () => setPreviewing(false);
                    audio.play().catch(() => setPreviewing(false));
                  } else {
                    toast.info('Preview not available yet');
                    setPreviewing(false);
                  }
                } catch { setPreviewing(false); }
              }}
            >
              {previewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              {previewing ? 'Playing...' : 'Play Voice Preview'}
            </Button>
          </div>
        )}

        {/* Actions */}
        <div className="px-4 pb-3 flex items-center gap-2">
          <Button
            size="sm"
            className="flex-1 text-xs h-8 bg-[#F7931A] hover:bg-[#F7931A]/90 text-black gap-1.5"
            onClick={() => onPurchase(voice)}
            disabled={purchasing === voice.voice_id}
            data-testid={`purchase-btn-${voice.voice_id}`}
          >
            {purchasing === voice.voice_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingCart className="w-3 h-3" />}
            Purchase for Hotel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CreateVoiceModal({ catalog, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', description: '', accent: 'American', style: 'Luxury Concierge', gender: 'female', price_hermes: 50, sample_text: '',
  });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!form.name || !form.description) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/marketplace/voices/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, creator_id: `creator_${Date.now()}` }),
      });
      if (res.ok) {
        toast.success('Voice created successfully');
        onCreated();
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create voice');
      }
    } catch {
      toast.error('Request failed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#0a0e1a] border border-white/10 rounded-2xl p-6 w-full max-w-lg space-y-4 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="create-voice-modal">
        <h2 className="text-base font-bold text-white heading-font flex items-center gap-2">
          <Mic className="w-4 h-4 text-purple-400" /> Create Voice Clone
        </h2>
        <p className="text-[11px] text-gray-500">Design your AI voice for the marketplace. Hotels can purchase and assign it to their agents.</p>

        <div className="space-y-3">
          <input
            value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            placeholder="Voice name (e.g. 'Luna — Luxury Concierge')"
            className="w-full h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-purple-500/30"
            data-testid="voice-name-input"
          />
          <textarea
            value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="Describe the voice character, tone, and ideal use..."
            rows={2}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-purple-500/30 resize-none"
            data-testid="voice-desc-input"
          />

          <div className="grid grid-cols-3 gap-2">
            <select value={form.accent} onChange={e => setForm(p => ({ ...p, accent: e.target.value }))}
              className="h-9 px-2 rounded-lg bg-white/5 border border-white/10 text-xs text-white focus:outline-none" data-testid="voice-accent-select">
              {(catalog.accents || []).map(a => <option key={a} value={a} className="bg-[#0a0e1a]">{a}</option>)}
            </select>
            <select value={form.style} onChange={e => setForm(p => ({ ...p, style: e.target.value }))}
              className="h-9 px-2 rounded-lg bg-white/5 border border-white/10 text-xs text-white focus:outline-none" data-testid="voice-style-select">
              {(catalog.styles || []).map(s => <option key={s} value={s} className="bg-[#0a0e1a]">{s}</option>)}
            </select>
            <select value={form.gender} onChange={e => setForm(p => ({ ...p, gender: e.target.value }))}
              className="h-9 px-2 rounded-lg bg-white/5 border border-white/10 text-xs text-white focus:outline-none" data-testid="voice-gender-select">
              {(catalog.genders || []).map(g => <option key={g} value={g} className="bg-[#0a0e1a]">{g}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <label className="text-[10px] text-gray-500 shrink-0">Price ($HERMES)</label>
            <input
              type="number" value={form.price_hermes} onChange={e => setForm(p => ({ ...p, price_hermes: Number(e.target.value) }))}
              min={0} step={5}
              className="w-24 h-8 px-2 rounded-lg bg-white/5 border border-white/10 text-sm text-[#F7931A] focus:outline-none text-center"
              data-testid="voice-price-input"
            />
          </div>

          <textarea
            value={form.sample_text} onChange={e => setForm(p => ({ ...p, sample_text: e.target.value }))}
            placeholder="Sample greeting text (optional — we'll generate one)"
            rows={2}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-purple-500/30 resize-none"
          />
        </div>

        {!catalog.fish_audio_ready && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/10">
            <Sparkles className="w-3 h-3 text-amber-400 shrink-0" />
            <p className="text-[9px] text-amber-400/70">Fish Audio integration pending. Voice will be created as a marketplace listing — AI cloning activates when API key is configured (10 seconds of audio = full voice clone).</p>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Button variant="outline" size="sm" className="flex-1 text-xs h-9 border-white/10 text-gray-400" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="flex-1 text-xs h-9 bg-purple-600 hover:bg-purple-700 text-white gap-1.5" onClick={handleCreate} disabled={creating || !form.name || !form.description} data-testid="create-voice-submit">
            {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Create Voice
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function VoiceMarketplacePage() {
  const [voices, setVoices] = useState([]);
  const [stats, setStats] = useState({});
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [filterAccent, setFilterAccent] = useState('');
  const [filterGender, setFilterGender] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [vRes, sRes, cRes] = await Promise.all([
        fetch(`${API}/api/marketplace/voices/browse${filterAccent ? `?accent=${filterAccent}` : ''}${filterGender ? `${filterAccent ? '&' : '?'}gender=${filterGender}` : ''}`),
        fetch(`${API}/api/marketplace/voices/stats`),
        fetch(`${API}/api/marketplace/voices/catalog`),
      ]);
      const [vData, sData, cData] = await Promise.all([vRes.json(), sRes.json(), cRes.json()]);
      setVoices(vData.voices || []);
      setStats(sData);
      setCatalog(cData);
    } catch {
      toast.error('Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  }, [filterAccent, filterGender]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePurchase = async (voice) => {
    setPurchasing(voice.voice_id);
    try {
      const res = await fetch(`${API}/api/marketplace/voices/${voice.voice_id}/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hotel_id: 'hotel_grand_marina', agent: 'bitsy' }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Purchased "${voice.name}" for ${data.price_hermes} $HERMES`);
        fetchData();
      } else {
        toast.error(data.detail || 'Purchase failed');
      }
    } catch {
      toast.error('Purchase request failed');
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] text-white" data-testid="voice-marketplace">
      {/* Header */}
      <div className="border-b border-white/5 bg-white/[0.01]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/creator-marketplace" className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 text-gray-400" />
            </Link>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/20 flex items-center justify-center">
                <Mic className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <h1 className="text-base font-bold heading-font">Voice Marketplace</h1>
                <p className="text-[10px] text-gray-500">Creator AI voices for hotel agents</p>
              </div>
            </div>
          </div>

          <Button
            size="sm"
            className="text-xs gap-1.5 h-8 bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => setShowCreate(true)}
            data-testid="create-voice-btn"
          >
            <Plus className="w-3 h-3" /> Create Voice
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard icon={Mic} label="Voices" value={stats.total_voices || 0} color="text-purple-400" />
          <StatCard icon={ShoppingCart} label="Purchases" value={stats.total_purchases || 0} color="text-[#F7931A]" />
          <StatCard icon={Users} label="Creators" value={stats.total_creators || 0} color="text-blue-400" />
          <StatCard icon={TrendingUp} label="Revenue" value={`${(stats.total_revenue_hermes || 0).toFixed(0)} H`} color="text-emerald-400" />
        </div>

        {/* Fish Audio banner */}
        {!stats.fish_audio_ready && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-purple-500/[0.04] border border-purple-500/10">
            <Sparkles className="w-4 h-4 text-purple-400 shrink-0" />
            <p className="text-[11px] text-gray-400 leading-relaxed">
              <span className="text-purple-400 font-medium">Fish Audio integration</span> is ready to activate. Once configured, creators can clone their real voices with just 10 seconds of audio — 150ms latency, 70+ languages, emotion control. Currently previewing with standard TTS.
            </p>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-gray-500">
            <Filter className="w-3 h-3" />
            <span className="text-[10px] uppercase tracking-wider">Filter:</span>
          </div>
          <select
            value={filterAccent}
            onChange={e => setFilterAccent(e.target.value)}
            className="h-7 px-2 rounded-lg bg-white/5 border border-white/10 text-xs text-white focus:outline-none"
            data-testid="filter-accent"
          >
            <option value="" className="bg-[#0a0e1a]">All Accents</option>
            {(catalog.accents || []).map(a => <option key={a} value={a} className="bg-[#0a0e1a]">{a}</option>)}
          </select>
          <select
            value={filterGender}
            onChange={e => setFilterGender(e.target.value)}
            className="h-7 px-2 rounded-lg bg-white/5 border border-white/10 text-xs text-white focus:outline-none"
            data-testid="filter-gender"
          >
            <option value="" className="bg-[#0a0e1a]">All Genders</option>
            {(catalog.genders || []).map(g => <option key={g} value={g} className="bg-[#0a0e1a]">{g}</option>)}
          </select>
        </div>

        {/* Voice Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {voices.map(v => (
            <VoiceCard
              key={v.voice_id}
              voice={v}
              onPurchase={handlePurchase}
              purchasing={purchasing}
            />
          ))}
        </div>

        {voices.length === 0 && (
          <div className="text-center py-16">
            <Mic className="w-8 h-8 text-gray-700 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No voices found</p>
            <p className="text-[10px] text-gray-600 mt-1">Adjust filters or create the first voice</p>
          </div>
        )}
      </div>

      {showCreate && <CreateVoiceModal catalog={catalog} onClose={() => setShowCreate(false)} onCreated={fetchData} />}
    </div>
  );
}
